import { useEffect, useRef, useState } from 'react';
import {
  CheckCircle2, Globe, Image as ImageIcon, KeyRound, Loader2, Mail, Palette, ShieldOff, Trash2, Upload, UserRound,
} from 'lucide-react';
import {
  useOperatorBranding,
  useUpdateOperatorBranding,
  useOperatorLogoPreview,
  useUploadOperatorLogo,
  useRemoveOperatorLogo,
  useOperatorSmtpProfile,
  useSaveOperatorSmtpCredentials,
  useDisableOperatorSmtp,
} from '../../api/hooks';
import { OPERATOR_SMTP_PROVIDERS } from '../../api/client';
import { useSessionStore } from '../../session/store';
import { refreshCurrentPortalBranding } from '../../branding/portalBrandingState';
import {
  isManagedLogoReference,
  OPERATOR_LOGO_ALLOWED_TYPES,
  OPERATOR_LOGO_MAX_BYTES,
} from '../../branding/brandingPresentation';
import algosysLogo from '../../assets/algosys_logo.png';
import { SectionHeader, Spinner } from '../../components/ui';
import { cn, fmtDateTime } from '../../lib/utils';

// Client-side checks mirror the backend OperatorBrandingUpdateRequest validators
// so obvious mistakes are caught before a request; the backend stays authoritative.
const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;
const DOMAIN_LABEL_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const IPV4_RE = /^\d{1,3}(?:\.\d{1,3}){3}$/;
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\x00-\x1f\x7f]/;

const FIELD_LIMITS = {
  business_name: 120,
  proposed_domain: 253,
};

const FIELD_LABELS = {
  business_name: 'Business name',
  primary_color: 'Primary colour',
  proposed_domain: 'Proposed custom domain',
};

// Controlled, fixed messages. Raw backend error bodies are never rendered.
const FIELD_REJECTED = {
  business_name: 'Enter a business name without control characters (120 characters max).',
  primary_color: 'Use the #RRGGBB format, for example #E8A33D.',
  proposed_domain: 'Enter a hostname only, such as portal.example.com. Do not include https://, a path, a port or a wildcard.',
};

const DOMAIN_STATUS = {
  NOT_CONFIGURED: {
    label: 'Not Configured',
    tone: 'muted',
    description: 'No custom domain has been proposed.',
  },
  PROPOSED: {
    label: 'Proposed',
    tone: 'info',
    description: 'This hostname is saved as a proposal only. AlgoSys must verify and approve it before it can be used, and your portal is not available on it.',
  },
  VERIFIED: {
    label: 'Verified',
    tone: 'info',
    description: 'AlgoSys has verified this hostname. It still needs AlgoSys approval and routing before it can be used.',
  },
  APPROVED: {
    label: 'Approved',
    tone: 'bull',
    description: 'AlgoSys has approved this hostname. Routing, DNS and HTTPS for it are managed by AlgoSys.',
  },
};
const UNKNOWN_DOMAIN_STATUS = {
  label: 'Unknown',
  tone: 'warn',
  description: 'The domain status could not be determined. Contact AlgoSys support.',
};

// Anything other than these states is treated as locked (fail closed).
const OPERATOR_EDITABLE_DOMAIN_STATES = new Set(['NOT_CONFIGURED', 'PROPOSED']);

const LOAD_ERROR_BY_STATUS = {
  403: 'This account is not allowed to manage branding settings.',
  404: 'Your Operator account could not be found or is not active.',
};
const LOAD_ERROR_FALLBACK = 'Your branding settings could not be loaded right now.';

const SAVE_ERROR_BY_STATUS = {
  403: 'This account is not allowed to change branding settings.',
  404: 'Your Operator account could not be found or is not active.',
};
const SAVE_ERROR_FALLBACK = 'Branding settings could not be saved. Your previous settings have not been changed.';

// Logo operations are a separate flow from Save Branding: their feedback lives
// in the Logo card and never disturbs the form. Raw backend error bodies are
// never rendered, and the backend validation remains authoritative.
const LOGO_UPLOAD_ERROR_BY_STATUS = {
  400: 'The upload was rejected. Choose a valid PNG, JPEG or WebP image.',
  403: 'This account is not allowed to change the logo.',
  404: 'Your Operator account could not be found or is not active.',
  409: 'The logo could not be updated because of a conflict. Nothing has been changed.',
  413: 'The image is too large. Use an image of 2 MiB or smaller, at most 2048 × 2048 pixels.',
  415: 'That file is not a supported image. Use a PNG, JPEG or WebP picture.',
  503: 'Logo storage is not available right now. Contact AlgoSys support to enable logo uploads.',
};
const LOGO_UPLOAD_ERROR_FALLBACK = 'The logo could not be updated right now. Nothing has been changed.';

const LOGO_REMOVE_ERROR_BY_STATUS = {
  403: 'This account is not allowed to change the logo.',
  404: 'Your Operator account could not be found or is not active.',
  503: 'Logo storage is not available right now. Try again later.',
};
const LOGO_REMOVE_ERROR_FALLBACK = 'The logo could not be removed right now. Nothing has been changed.';

function controlledLoadError(error) {
  return LOAD_ERROR_BY_STATUS[error?.status] ?? LOAD_ERROR_FALLBACK;
}

function conflictMessage(error) {
  const detail = typeof error?.data?.detail === 'string' ? error.data.detail : '';
  if (/verified or approved/i.test(detail)) {
    return 'Your domain has already been verified or approved, so only AlgoSys can change it. Your other settings have not been changed.';
  }
  if (/already configured/i.test(detail)) {
    return 'This hostname is already used by another account. Choose a different hostname. Your previous settings have not been changed.';
  }
  return 'These settings conflict with an existing configuration. Your previous settings have not been changed.';
}

// FastAPI validation errors carry `loc: ['body', <field>]`; map them onto our
// own per-field messages.
function rejectedFieldErrors(error) {
  const detail = error?.data?.detail;
  if (!Array.isArray(detail)) return {};
  const fields = {};
  for (const item of detail) {
    const field = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : null;
    if (typeof field === 'string' && FIELD_REJECTED[field]) fields[field] = FIELD_REJECTED[field];
  }
  return fields;
}

function normalizeText(value) {
  return String(value ?? '').trim();
}

function normalizeDomain(value) {
  return normalizeText(value).replace(/\.+$/, '').toLowerCase();
}

function normalizeField(field, value) {
  if (field === 'primary_color') return normalizeText(value).toUpperCase();
  if (field === 'proposed_domain') return normalizeDomain(value);
  return normalizeText(value);
}

function validateDomain(candidate) {
  if (CONTROL_RE.test(candidate) || /[/\\:@?#*\s]/.test(candidate)) return FIELD_REJECTED.proposed_domain;
  if (candidate.length > FIELD_LIMITS.proposed_domain || !candidate.includes('.')) {
    return 'Enter a fully qualified hostname, such as portal.example.com.';
  }
  if (IPV4_RE.test(candidate)) return 'An IP address cannot be used. Enter a hostname.';
  // Non-ASCII (internationalised) names are left to the backend's IDNA check.
  // eslint-disable-next-line no-control-regex
  if (/^[\x00-\x7f]*$/.test(candidate) && candidate.split('.').some((label) => !DOMAIN_LABEL_RE.test(label))) {
    return 'The hostname contains an invalid part. Use letters, numbers and hyphens between the dots.';
  }
  return null;
}

function validateField(field, value) {
  if (value === '') return null; // blank clears the field back to the AlgoSys default
  if (CONTROL_RE.test(value)) return FIELD_REJECTED[field];
  const limit = FIELD_LIMITS[field];
  if (limit && value.length > limit) return `${FIELD_LABELS[field]} must be ${limit} characters or fewer.`;
  if (field === 'primary_color' && !HEX_COLOR_RE.test(value)) return FIELD_REJECTED.primary_color;
  if (field === 'proposed_domain') return validateDomain(value);
  return null;
}

function initialForm(branding) {
  return {
    business_name: branding?.business_name ?? '',
    primary_color: branding?.primary_color ?? '',
    proposed_domain: branding?.proposed_domain ?? '',
  };
}

function StatusPill({ tone, children }) {
  return (
    <span className={cn(
      'inline-flex items-center px-2 py-0.5 rounded border text-2xs font-mono uppercase tracking-wide',
      tone === 'bull' && 'text-bull bg-bull/10 border-bull/30',
      tone === 'warn' && 'text-warn bg-warn/10 border-warn/30',
      tone === 'info' && 'text-info bg-info/10 border-info/30',
      tone === 'muted' && 'text-txt-muted border-bg-border',
    )}>
      {children}
    </span>
  );
}

function Field({ label, hint, error, children }) {
  return (
    <label className="space-y-1 block min-w-0">
      <span className="text-2xs font-mono text-txt-muted uppercase tracking-widest">{label}</span>
      {children}
      {error ? (
        <span className="block text-2xs text-bear">{error}</span>
      ) : hint ? (
        <span className="block text-2xs text-txt-muted">{hint}</span>
      ) : null}
    </label>
  );
}

function BrandingForm({ branding, onSaved, onEdit }) {
  const { mutateAsync: updateBranding, isPending: saving } = useUpdateOperatorBranding();
  const [form, setForm] = useState(() => initialForm(branding));
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [formNotice, setFormNotice] = useState(null);

  const domainStatusKey = branding?.domain_status;
  const domainStatus = DOMAIN_STATUS[domainStatusKey] ?? UNKNOWN_DOMAIN_STATUS;
  const domainEditable = OPERATOR_EDITABLE_DOMAIN_STATES.has(domainStatusKey);
  const routingEnabled = branding?.domain_routing_enabled === true;
  const pickerColor = HEX_COLOR_RE.test(form.primary_color.trim())
    ? form.primary_color.trim().toLowerCase()
    : (HEX_COLOR_RE.test(branding?.primary_color ?? '') ? branding.primary_color.toLowerCase() : '#000000');

  const editableFields = [
    'business_name',
    'primary_color',
    ...(domainEditable ? ['proposed_domain'] : []),
  ];

  function setValue(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setFormError(null);
    setFormNotice(null);
    onEdit();
  }

  function collectChanges() {
    const changes = {};
    const errors = {};
    for (const field of editableFields) {
      const next = normalizeField(field, form[field]);
      const current = normalizeField(field, branding?.[field]);
      if (next === current) continue;
      const error = validateField(field, next);
      if (error) errors[field] = error;
      else changes[field] = next === '' ? null : next;
    }
    return { changes, errors };
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setFormError(null);
    setFormNotice(null);
    onEdit();
    const { changes, errors } = collectChanges();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setFormError('Check the highlighted fields. Nothing has been saved.');
      return;
    }
    if (Object.keys(changes).length === 0) {
      setFormNotice('There are no changes to save.');
      return;
    }
    try {
      await updateBranding(changes);
      const domainChanged = Object.prototype.hasOwnProperty.call(changes, 'proposed_domain');
      onSaved(
        domainChanged && changes.proposed_domain
          ? 'Branding settings saved. The hostname is saved as a proposal only and is not active until AlgoSys verifies and approves it.'
          : 'Branding settings saved.',
      );
    } catch (err) {
      if (err?.status === 409) {
        setFormError(conflictMessage(err));
      } else if (err?.status === 400 || err?.status === 422) {
        const rejected = rejectedFieldErrors(err);
        setFieldErrors(rejected);
        setFormError(Object.keys(rejected).length > 0
          ? 'Some values were not accepted. Check the highlighted fields. Your previous settings have not been changed.'
          : 'Some values were not accepted. Your previous settings have not been changed.');
      } else {
        setFormError(SAVE_ERROR_BY_STATUS[err?.status] ?? SAVE_ERROR_FALLBACK);
      }
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <section className="border border-bg-border rounded-lg p-4 space-y-4" aria-label="Business details">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-medium text-txt-primary">
            <Palette size={15} />
            Business Details
          </div>
          {branding?.is_default === true && <StatusPill tone="muted">AlgoSys defaults</StatusPill>}
        </div>
        {branding?.is_default === true && (
          <p className="text-xs text-txt-secondary">
            You have not saved any branding yet, so the AlgoSys default values are shown.
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Business name" hint="Leave blank to use the AlgoSys default." error={fieldErrors.business_name}>
            <input
              type="text"
              value={form.business_name}
              onChange={(e) => setValue('business_name', e.target.value)}
              maxLength={FIELD_LIMITS.business_name}
              autoComplete="organization"
              aria-invalid={Boolean(fieldErrors.business_name)}
              className="input-base w-full text-xs"
            />
          </Field>

          <Field label="Primary colour" hint="Format #RRGGBB." error={fieldErrors.primary_color}>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={pickerColor}
                onChange={(e) => setValue('primary_color', e.target.value.toUpperCase())}
                aria-label="Primary colour picker"
                className="h-8 w-10 shrink-0 rounded border border-bg-border bg-transparent p-0.5 cursor-pointer"
              />
              <input
                type="text"
                value={form.primary_color}
                onChange={(e) => setValue('primary_color', e.target.value)}
                maxLength={7}
                placeholder="#E8A33D"
                spellCheck={false}
                autoComplete="off"
                aria-invalid={Boolean(fieldErrors.primary_color)}
                aria-label="Primary colour hex value"
                className="input-base w-full text-xs font-mono"
              />
            </div>
          </Field>
        </div>

      </section>

      <section className="border border-bg-border rounded-lg p-4 space-y-4" aria-label="Custom domain">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-medium text-txt-primary">
            <Globe size={15} />
            Custom Domain
          </div>
          <StatusPill tone={domainStatus.tone}>{domainStatus.label}</StatusPill>
        </div>

        <p className="text-xs text-txt-secondary">{domainStatus.description}</p>

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
          <div className="min-w-0">
            <dt className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Domain routing</dt>
            <dd className="text-xs text-txt-primary mt-0.5">
              {routingEnabled ? 'Enabled by AlgoSys' : 'Not enabled'}
            </dd>
          </div>
          {branding?.domain_verified_at && (
            <div className="min-w-0">
              <dt className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Verified</dt>
              <dd className="text-xs text-txt-primary mt-0.5">{fmtDateTime(branding.domain_verified_at)}</dd>
            </div>
          )}
          {branding?.domain_approved_at && (
            <div className="min-w-0">
              <dt className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Approved</dt>
              <dd className="text-xs text-txt-primary mt-0.5">{fmtDateTime(branding.domain_approved_at)}</dd>
            </div>
          )}
        </dl>

        {domainEditable ? (
          <Field
            label="Proposed hostname"
            hint="Hostname only, for example portal.example.com. Saving a hostname only proposes it; leave blank to remove the proposal."
            error={fieldErrors.proposed_domain}
          >
            <input
              type="text"
              value={form.proposed_domain}
              onChange={(e) => setValue('proposed_domain', e.target.value)}
              maxLength={FIELD_LIMITS.proposed_domain}
              placeholder="portal.example.com"
              spellCheck={false}
              autoComplete="off"
              aria-invalid={Boolean(fieldErrors.proposed_domain)}
              className="input-base w-full text-xs font-mono"
            />
          </Field>
        ) : (
          <div className="space-y-1">
            <div className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Hostname</div>
            <div className="text-xs text-txt-primary font-mono break-all">{branding?.proposed_domain || '—'}</div>
            <p className="text-2xs text-txt-muted">
              This hostname can no longer be changed here. Contact AlgoSys support to change it.
            </p>
          </div>
        )}

        <p className="text-2xs text-txt-muted border-t border-bg-border/60 pt-3">
          Domain verification, approval and routing are performed by AlgoSys only.
        </p>
      </section>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="submit"
          disabled={saving}
          className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-50"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
          {saving ? 'Saving…' : 'Save Branding'}
        </button>
      </div>

      {formError && (
        <p role="alert" className="text-xs text-bear bg-bear/5 border border-bear/30 rounded px-3 py-2">{formError}</p>
      )}
      {formNotice && (
        <p className="text-xs text-txt-secondary border border-bg-border rounded px-3 py-2">{formNotice}</p>
      )}
    </form>
  );
}

function fmtFileSize(bytes) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
  if (bytes >= 1024) return `${Math.max(1, Math.round(bytes / 1024))} KiB`;
  return `${bytes} B`;
}

// Object URL for the authenticated logo preview blob. The URL is revoked on
// replacement, removal, unmount and account switching (a new user or reference
// remounts this component), so no blob ever outlives its owning session.
function useBlobObjectUrl(blob) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!blob) {
      setUrl((prev) => (prev === null ? prev : null));
      return undefined;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);
  return url;
}

// Logo management card. Deliberately outside the Save Branding form: logo
// changes never submit or reset the branding fields, and unsaved field edits
// survive a logo upload or removal.
function LogoSection({ branding, onLogoChanged }) {
  // Only a backend-managed reference is a servable logo; legacy metadata is
  // never treated as an image and is shown as "no custom logo".
  const managed = isManagedLogoReference(branding?.logo_reference);
  const { data: logoBlob, error: previewError, isLoading: previewLoading } = useOperatorLogoPreview({
    logoReference: managed ? branding.logo_reference : null,
  });
  const previewUrl = useBlobObjectUrl(logoBlob);
  const { mutateAsync: uploadLogo, isPending: uploading } = useUploadOperatorLogo();
  const { mutateAsync: removeLogo, isPending: removing } = useRemoveOperatorLogo();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectionError, setSelectionError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const busy = uploading || removing;

  function clearFeedback() {
    setActionError(null);
    setNotice(null);
  }

  function resetFileInput() {
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function chooseFile(file) {
    clearFeedback();
    setConfirmingRemove(false);
    resetFileInput();
    if (!file) {
      setSelectedFile(null);
      setSelectionError(null);
      return;
    }
    // Convenience checks only; the backend re-validates type and bytes.
    if (!OPERATOR_LOGO_ALLOWED_TYPES.includes(file.type)) {
      setSelectedFile(null);
      setSelectionError('Choose a PNG, JPEG or WebP image.');
      return;
    }
    if (file.size === 0) {
      setSelectedFile(null);
      setSelectionError('The selected file is empty.');
      return;
    }
    if (file.size > OPERATOR_LOGO_MAX_BYTES) {
      setSelectedFile(null);
      setSelectionError('The image must be 2 MiB or smaller.');
      return;
    }
    setSelectedFile(file);
    setSelectionError(null);
  }

  function cancelSelection() {
    clearFeedback();
    resetFileInput();
    setSelectedFile(null);
    setSelectionError(null);
  }

  async function handleUpload() {
    if (!selectedFile || busy) return;
    clearFeedback();
    try {
      await uploadLogo(selectedFile);
      setSelectedFile(null);
      resetFileInput();
      setNotice(managed ? 'Logo replaced.' : 'Logo uploaded.');
      // Refresh the current approved portal's branding and logo live; on the
      // main portal (or any non-branded origin) this is a safe no-op.
      onLogoChanged();
    } catch (err) {
      setActionError(LOGO_UPLOAD_ERROR_BY_STATUS[err?.status] ?? LOGO_UPLOAD_ERROR_FALLBACK);
    }
  }

  async function handleRemove() {
    if (busy) return;
    clearFeedback();
    setConfirmingRemove(false);
    try {
      await removeLogo();
      setSelectedFile(null);
      resetFileInput();
      setNotice('Logo removed. Your portal now uses the AlgoSys logo.');
      onLogoChanged();
    } catch (err) {
      setActionError(LOGO_REMOVE_ERROR_BY_STATUS[err?.status] ?? LOGO_REMOVE_ERROR_FALLBACK);
    }
  }

  return (
    <section className="border border-bg-border rounded-lg p-4 space-y-4" aria-label="Logo">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-txt-primary">
          <ImageIcon size={15} />
          Logo
        </div>
        <StatusPill tone={managed ? 'info' : 'muted'}>
          {managed ? 'Custom logo' : 'AlgoSys default'}
        </StatusPill>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex h-20 w-36 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-bg-border bg-bg-base">
          {previewLoading ? (
            <Loader2 size={16} className="animate-spin text-txt-muted" />
          ) : previewUrl ? (
            <img
              src={previewUrl}
              alt="Your current logo preview"
              className="max-h-20 max-w-[9rem] object-contain"
            />
          ) : (
            <img
              src={algosysLogo}
              alt="AlgoSys platform logo"
              className="max-h-16 max-w-[8rem] object-contain"
            />
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-1 text-xs">
          {managed ? (
            previewError ? (
              <p className="text-bear">
                Your saved logo could not be loaded right now. You can still replace or remove it.
              </p>
            ) : (
              <p className="text-txt-secondary">This is the logo currently saved for your account.</p>
            )
          ) : (
            <p className="text-txt-secondary">
              No custom logo is saved, so your portal uses the AlgoSys logo.
            </p>
          )}
          <p className="text-2xs text-txt-muted">
            Customers see your logo only on your AlgoSys-approved custom domain with routing
            enabled. Everywhere else, the AlgoSys logo is used.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept={OPERATOR_LOGO_ALLOWED_TYPES.join(',')}
          className="hidden"
          onChange={(e) => chooseFile(e.target.files ? e.target.files[0] : null)}
          aria-label="Logo image file"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          className="btn-secondary text-xs"
        >
          {managed ? 'Choose replacement image' : 'Choose image'}
        </button>
        {selectedFile && (
          <>
            <span
              className="max-w-[12rem] truncate text-xs text-txt-muted"
              title={selectedFile.name}
            >
              {selectedFile.name} · {fmtFileSize(selectedFile.size)}
            </span>
            <button type="button" onClick={cancelSelection} disabled={busy} className="btn-secondary text-xs">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUpload}
              disabled={busy}
              className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-50"
            >
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {uploading ? 'Uploading…' : (managed ? 'Replace logo' : 'Upload logo')}
            </button>
          </>
        )}
        {managed && !selectedFile && !confirmingRemove && (
          <button
            type="button"
            onClick={() => { clearFeedback(); setConfirmingRemove(true); }}
            disabled={busy}
            className="btn-secondary text-xs flex items-center gap-1.5"
          >
            <Trash2 size={12} />
            Remove logo
          </button>
        )}
        {confirmingRemove && (
          <span className="inline-flex flex-wrap items-center gap-2 text-xs text-txt-secondary">
            Remove your logo and return to the AlgoSys logo?
            <button
              type="button"
              onClick={handleRemove}
              disabled={busy}
              className="btn-primary text-xs"
            >
              {removing ? 'Removing…' : 'Remove'}
            </button>
            <button
              type="button"
              onClick={() => setConfirmingRemove(false)}
              disabled={busy}
              className="btn-secondary text-xs"
            >
              Keep
            </button>
          </span>
        )}
      </div>

      {selectionError && <p className="text-xs text-bear">{selectionError}</p>}
      {actionError && (
        <p role="alert" className="text-xs text-bear bg-bear/5 border border-bear/30 rounded px-3 py-2">
          {actionError}
        </p>
      )}
      {notice && (
        <p role="status" className="text-xs text-bull bg-bull/5 border border-bull/30 rounded px-3 py-2">
          {notice}
        </p>
      )}

      <p className="text-2xs text-txt-muted border-t border-bg-border/60 pt-3">
        PNG, JPEG or WebP · up to 2 MiB · at most 2048 × 2048 pixels and four million pixels in
        total · single frame · transparency is kept and metadata is removed.
      </p>
    </section>
  );
}
// The registered Operator email and phone come from the Super Admin-managed
// account record (returned as support_email/support_phone). They are display
// only here and are never part of the Save Branding request.
function ContactDetailsSection({ branding }) {
  return (
    <section className="border border-bg-border rounded-lg p-4 space-y-3" aria-label="Operator contact details">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-txt-primary">
          <UserRound size={15} />
          Operator Contact Details
        </div>
        <StatusPill tone="muted">Read only</StatusPill>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
        <div className="min-w-0">
          <dt className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Registered email</dt>
          <dd className="text-xs text-txt-primary mt-0.5 break-all">{branding?.support_email || '—'}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Registered phone</dt>
          <dd className="text-xs text-txt-primary mt-0.5 break-all">{branding?.support_phone || '—'}</dd>
        </div>
      </dl>
      <p className="text-2xs text-txt-muted border-t border-bg-border/60 pt-3">
        These are the email and phone registered for your Operator account. Only the AlgoSys Super Admin
        can change them.
      </p>
    </section>
  );
}

const SMTP_PROVIDER_LABELS = {
  gmail: 'Gmail',
  microsoft: 'Microsoft / Outlook',
};

const SMTP_SECURITY_LABELS = {
  implicit_tls: 'TLS',
  starttls: 'STARTTLS',
};

const SMTP_STATUS = {
  not_configured: {
    label: 'Not Configured',
    tone: 'muted',
    description: 'No SMTP credentials are saved for your account.',
  },
  verification_required: {
    label: 'Verification Required',
    tone: 'warn',
    description: 'SMTP is not active. Your saved credentials are not verified, or SMTP was disabled. Enter your app password and choose Replace SMTP Credentials to verify and enable it.',
  },
  credential_unavailable: {
    label: 'Credential Unavailable',
    tone: 'warn',
    description: 'Your saved SMTP credential cannot be used right now. Save your app password again or contact AlgoSys support.',
  },
  identity_changed: {
    label: 'Operator Email Changed',
    tone: 'warn',
    description: 'Your registered Operator email changed after these credentials were saved. Save an app password for the current registered email.',
  },
  disabled: {
    label: 'Disabled',
    tone: 'muted',
    description: 'SMTP is disabled for your account. Customer emails are not sent through this configuration.',
  },
  ready: {
    label: 'Ready',
    tone: 'bull',
    description: 'Your SMTP credentials are verified and enabled. Emails to your customers are sent from your registered Operator email.',
  },
};
const UNKNOWN_SMTP_STATUS = {
  label: 'Unknown',
  tone: 'warn',
  description: 'The SMTP status could not be determined. Contact AlgoSys support.',
};

// Ready requires every backend readiness flag; an HTTP 200 alone proves nothing.
function isSmtpReady(profile) {
  return profile?.status === 'ready'
    && profile?.ready === true
    && profile?.enabled === true
    && profile?.verified === true
    && profile?.credential_configured === true;
}

function smtpStatusFor(profile) {
  // Fail closed: inconsistent flags are shown as Unknown, never as Ready.
  if (profile?.status === 'ready') return isSmtpReady(profile) ? SMTP_STATUS.ready : UNKNOWN_SMTP_STATUS;
  if (profile?.enabled === true || profile?.ready === true) return UNKNOWN_SMTP_STATUS;
  return SMTP_STATUS[profile?.status] ?? UNKNOWN_SMTP_STATUS;
}

const SMTP_ERROR_BY_STATUS = {
  400: 'The SMTP configuration was rejected. Your previous SMTP configuration has not been changed.',
  401: 'Authentication is required. Sign in again and retry.',
  403: 'Operator access is denied for SMTP settings on this account.',
  404: 'Your Operator account is unavailable.',
  409: 'The SMTP configuration changed during verification. New credentials were not saved. Reload and try again.',
  422: 'The SMTP configuration is invalid. Check the provider and app password.',
  429: 'Too many SMTP verification attempts. Try again later. Your previous SMTP configuration is unchanged.',
  502: 'SMTP verification failed. Check your app password and registered sender email. New credentials were not saved.',
  503: 'SMTP verification is temporarily unavailable. New credentials were not saved; your previous configuration is unchanged.',
};
const SMTP_NETWORK_ERROR = 'The request did not complete, so it is not confirmed that your previous SMTP configuration changed. Reload this page to check the current status.';
const SMTP_ERROR_FALLBACK = 'The SMTP request could not be completed. Reload this page to check the current status.';
const SMTP_LOAD_ERROR_FALLBACK = 'Your SMTP settings could not be loaded right now.';

function smtpErrorMessage(err) {
  if (err?.status == null) return SMTP_NETWORK_ERROR;
  return SMTP_ERROR_BY_STATUS[err.status] ?? SMTP_ERROR_FALLBACK;
}

// Mirrors the backend app-password checks; the backend stays authoritative.
const SMTP_PASSWORD_MIN = 8;
const SMTP_PASSWORD_MAX = 256;
// eslint-disable-next-line no-control-regex
const SMTP_PASSWORD_FORBIDDEN_RE = /[\r\n\x00]/;

function validateSmtpPassword(value) {
  if (!value) return 'Enter your SMTP app password.';
  if (value.length < SMTP_PASSWORD_MIN || value.length > SMTP_PASSWORD_MAX) {
    return `The app password must be ${SMTP_PASSWORD_MIN} to ${SMTP_PASSWORD_MAX} characters.`;
  }
  if (SMTP_PASSWORD_FORBIDDEN_RE.test(value)) return 'The app password contains characters that are not allowed.';
  return null;
}

// Operator SMTP credential card. Separate from the Save Branding form: its
// requests never submit, reset or invalidate the branding fields. The app
// password lives only in this component's state and is cleared once a request
// settles; the backend never returns it.
function SmtpSection() {
  const { data: profile, isLoading, error: loadError } = useOperatorSmtpProfile();
  const {
    mutateAsync: saveCredentials, isPending: saving, reset: resetSave,
  } = useSaveOperatorSmtpCredentials();
  const {
    mutateAsync: disableSmtp, isPending: disabling, reset: resetDisable,
  } = useDisableOperatorSmtp();
  const [provider, setProvider] = useState(null);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [confirmingDisable, setConfirmingDisable] = useState(false);
  // Synchronous guard: isPending only updates on the next render, so a fast
  // double click could otherwise send two requests.
  const inFlight = useRef(false);
  const busy = saving || disabling;

  // Drop any mutation state (which briefly holds the submitted app password)
  // when the card unmounts, including on account switch.
  useEffect(() => () => {
    resetSave?.();
    resetDisable?.();
  }, [resetSave, resetDisable]);

  const configured = profile?.credential_configured === true;
  const savedProvider = OPERATOR_SMTP_PROVIDERS.includes(profile?.provider) ? profile.provider : null;
  const selectedProvider = provider ?? savedProvider ?? OPERATOR_SMTP_PROVIDERS[0];
  const status = smtpStatusFor(profile);

  function clearFeedback() {
    setActionError(null);
    setNotice(null);
  }

  async function handleSave(event) {
    event.preventDefault();
    if (busy || inFlight.current) return;
    clearFeedback();
    setConfirmingDisable(false);
    const invalid = validateSmtpPassword(password);
    setPasswordError(invalid);
    if (invalid) return;
    inFlight.current = true;
    try {
      const result = await saveCredentials({ provider: selectedProvider, app_password: password });
      setProvider(null);
      if (isSmtpReady(result)) {
        setNotice('SMTP credentials verified and enabled successfully.');
      } else {
        setActionError('The SMTP save response did not confirm that SMTP is verified and enabled. Reload this page to check the current status.');
      }
    } catch (err) {
      setActionError(smtpErrorMessage(err));
    } finally {
      inFlight.current = false;
      setPassword('');
      resetSave?.();
    }
  }

  async function handleDisable() {
    if (busy || inFlight.current) return;
    clearFeedback();
    setConfirmingDisable(false);
    inFlight.current = true;
    try {
      await disableSmtp();
      setNotice('SMTP configuration disabled. Customer emails will not be sent through it until you save and verify credentials again.');
    } catch (err) {
      setActionError(smtpErrorMessage(err));
    } finally {
      inFlight.current = false;
      setPassword('');
      setPasswordError(null);
      resetDisable?.();
    }
  }

  return (
    <section className="border border-bg-border rounded-lg p-4 space-y-4" aria-label="Email / SMTP configuration">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-txt-primary">
          <Mail size={15} />
          Email / SMTP Configuration
        </div>
        {!isLoading && !loadError && profile && <StatusPill tone={status.tone}>{status.label}</StatusPill>}
      </div>

      {isLoading ? (
        <Spinner />
      ) : loadError ? (
        <p className="text-xs text-bear">
          {loadError.status === 403 || loadError.status === 404
            ? SMTP_ERROR_BY_STATUS[loadError.status]
            : SMTP_LOAD_ERROR_FALLBACK}
        </p>
      ) : !profile ? (
        <p className="text-xs text-bear">{SMTP_LOAD_ERROR_FALLBACK}</p>
      ) : (
        <>
          <p className="text-xs text-txt-secondary">{status.description}</p>

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
            <div className="min-w-0">
              <dt className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Registered sender email</dt>
              <dd className="text-xs text-txt-primary mt-0.5 break-all">{profile.email_from || '—'}</dd>
            </div>
            {configured && (
              <>
                <div className="min-w-0">
                  <dt className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Saved provider</dt>
                  <dd className="text-xs text-txt-primary mt-0.5">{SMTP_PROVIDER_LABELS[savedProvider] ?? '—'}</dd>
                </div>
                {profile.smtp_host && (
                  <div className="min-w-0">
                    <dt className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Server</dt>
                    <dd className="text-xs text-txt-primary mt-0.5 font-mono break-all">
                      {profile.smtp_host}{profile.smtp_port ? `:${profile.smtp_port}` : ''}
                      {SMTP_SECURITY_LABELS[profile.transport_security]
                        ? ` · ${SMTP_SECURITY_LABELS[profile.transport_security]}`
                        : ''}
                    </dd>
                  </div>
                )}
                {profile.updated_at && (
                  <div className="min-w-0">
                    <dt className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Last updated</dt>
                    <dd className="text-xs text-txt-primary mt-0.5">{fmtDateTime(profile.updated_at)}</dd>
                  </div>
                )}
              </>
            )}
          </dl>

          <form onSubmit={handleSave} noValidate className="space-y-3" aria-label="SMTP credentials">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="SMTP provider">
                <select
                  value={selectedProvider}
                  onChange={(e) => { clearFeedback(); setProvider(e.target.value); }}
                  disabled={busy}
                  className="input-field w-full text-xs"
                >
                  {OPERATOR_SMTP_PROVIDERS.map((value) => (
                    <option key={value} value={value}>{SMTP_PROVIDER_LABELS[value]}</option>
                  ))}
                </select>
              </Field>
              <Field
                label="SMTP app password"
                hint={configured
                  ? 'Your saved app password is never shown. Enter a new one to replace it.'
                  : 'Use an app password created for your registered email, not your mailbox password.'}
                error={passwordError}
              >
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError(null);
                    clearFeedback();
                  }}
                  maxLength={SMTP_PASSWORD_MAX}
                  placeholder={configured ? 'Enter a new app password' : 'Enter your app password'}
                  autoComplete="new-password"
                  spellCheck={false}
                  disabled={busy}
                  aria-invalid={Boolean(passwordError)}
                  className="input-base w-full text-xs"
                />
              </Field>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="submit"
                disabled={busy}
                className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-50"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />}
                {saving ? 'Saving…' : (configured ? 'Replace SMTP Credentials' : 'Save SMTP Credentials')}
              </button>
              {configured && !confirmingDisable && (
                <button
                  type="button"
                  onClick={() => { clearFeedback(); setConfirmingDisable(true); }}
                  disabled={busy}
                  className="btn-secondary text-xs flex items-center gap-1.5"
                >
                  <ShieldOff size={12} />
                  Disable SMTP Configuration
                </button>
              )}
              {confirmingDisable && (
                <span className="inline-flex flex-wrap items-center gap-2 text-xs text-txt-secondary">
                  Disable SMTP? Customer emails will stop being sent through this configuration until you save and verify credentials again.
                  <button type="button" onClick={handleDisable} disabled={busy} className="btn-primary text-xs">
                    {disabling ? 'Disabling…' : 'Confirm disable'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingDisable(false)}
                    disabled={busy}
                    className="btn-secondary text-xs"
                  >
                    Cancel
                  </button>
                </span>
              )}
            </div>
          </form>

          {actionError && (
            <p role="alert" className="text-xs text-bear bg-bear/5 border border-bear/30 rounded px-3 py-2">
              {actionError}
            </p>
          )}
          {notice && (
            <p role="status" className="text-xs text-bull bg-bull/5 border border-bull/30 rounded px-3 py-2">
              {notice}
            </p>
          )}

          <p className="text-2xs text-txt-muted border-t border-bg-border/60 pt-3">
            Emails can only use your registered Operator email as the sender. Server, port and encryption are
            fixed by the selected provider. Credentials are verified automatically when saved, and a successful save
            enables SMTP; failed verification does not save new credentials. Emails to your customers are sent through
            this configuration, and disabling it stops that delivery. No test email is sent, and verification does not
            guarantee that emails reach the inbox.
          </p>
        </>
      )}
    </section>
  );
}
// Remount the form only when the saved field values change (or after a
// successful save, via the caller's revision counter), so a successful save
// shows the backend-normalised values while an unchanged refetch — including a
// logo upload or removal, which changes only updated_at/logo_reference — never
// discards in-progress edits.
function brandingFormKey(branding) {
  return JSON.stringify([
    branding?.operator_id ?? null,
    branding?.business_name ?? null,
    branding?.primary_color ?? null,
    branding?.proposed_domain ?? null,
    branding?.domain_status ?? null,
    branding?.domain_routing_enabled ?? null,
  ]);
}

export default function OperatorBrandingSettingsPage() {
  const role = useSessionStore((s) => s.role);
  const currentUserId = useSessionStore((s) => s.user?.id);
  // Exact Operator role only. The backend remains the authority on every
  // request; this is a UI guard so the interface is not shown to other roles.
  const isOperator = role === 'operator';

  const { data: branding, isLoading, error } = useOperatorBranding({ enabled: isOperator });
  const [notice, setNotice] = useState(null);
  // Bumped after each successful Save Branding so the form remounts with the
  // backend-normalised values. Logo changes deliberately do NOT bump it, so an
  // upload or removal refreshes the branding query without discarding unsaved
  // edits to the unrelated fields.
  const [formRevision, setFormRevision] = useState(0);

  if (!isOperator) {
    return (
      <div className="p-6 max-w-3xl">
        <SectionHeader title="Branding Settings" />
        <p className="text-xs text-txt-muted mt-3">
          Branding settings are available to Operator accounts only.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-3xl">
      <SectionHeader
        title="Branding Settings"
        subtitle="Set the business details shown to your customers, propose a custom domain and manage your SMTP credentials."
      />

      {isLoading ? (
        <Spinner />
      ) : error ? (
        <p className="text-xs text-bear">{controlledLoadError(error)}</p>
      ) : branding ? (
        <>
          {notice && (
            <p role="status" className="text-xs text-bull bg-bull/5 border border-bull/30 rounded px-3 py-2">{notice}</p>
          )}
          {branding.updated_at && (
            <p className="text-2xs text-txt-muted">Last updated {fmtDateTime(branding.updated_at)}</p>
          )}
          {/* Remounted per Operator so a selected file or pending logo action can
              never carry over into another account's session. */}
          <LogoSection
            key={branding.operator_id}
            branding={branding}
            onLogoChanged={refreshCurrentPortalBranding}
          />
          <BrandingForm
            key={`${brandingFormKey(branding)}|${formRevision}`}
            branding={branding}
            onSaved={(message) => {
              setNotice(message);
              setFormRevision((r) => r + 1);
            }}
            onEdit={() => setNotice(null)}
          />
          <ContactDetailsSection branding={branding} />
          {/* Keyed by the signed-in account so an unsent app password, a pending
              confirmation or SMTP feedback never carries over to another Operator. */}
          <SmtpSection key={`${currentUserId ?? 'none'}|${branding.operator_id}`} />
        </>
      ) : (
        <p className="text-xs text-bear">{LOAD_ERROR_FALLBACK}</p>
      )}
    </div>
  );
}
